import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, Upload, Link, Video, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ModuleManager({ user }) {
  const [modules, setModules] = useState([]);
  const [editingModule, setEditingModule] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    order: 1,
    master_video_url: '',
    master_video_type: '',
    essential_materials: [],
    complementary_materials: [],
    essential_content: { topics: [] },
    complementary_content: { topics: [] },
    activities: [],
    references: [],
    live_session_url: ''
  });
  const [newMaterial, setNewMaterial] = useState({ title: '', url: '', type: 'link' });

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/modules`, { withCredentials: true });
      setModules(response.data);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const handleEdit = (module) => {
    setEditingModule(module.module_id);
    setFormData({
      ...module,
      master_video_url: module.master_video_url || '',
      master_video_type: module.master_video_type || '',
      essential_materials: module.essential_materials || [],
      complementary_materials: module.complementary_materials || [],
      essential_content: module.essential_content || { topics: [] },
      complementary_content: module.complementary_content || { topics: [] },
      activities: module.activities || [],
      references: module.references || []
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      order: modules.length + 1,
      master_video_url: '',
      master_video_type: '',
      essential_materials: [],
      complementary_materials: [],
      essential_content: { topics: [] },
      complementary_content: { topics: [] },
      activities: [],
      references: [],
      live_session_url: ''
    });
  };

  const addMaterialLink = async (materialType) => {
    if (!newMaterial.title || !newMaterial.url) {
      toast.error('Preencha título e URL');
      return;
    }
    const field = materialType === 'essential' ? 'essential_materials' : 'complementary_materials';
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), { ...newMaterial, id: `temp_${Date.now()}` }]
    }));
    setNewMaterial({ title: '', url: '', type: 'link' });
    toast.success('Link adicionado');
  };

  const removeMaterial = (materialType, materialId) => {
    const field = materialType === 'essential' ? 'essential_materials' : 'complementary_materials';
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(m => m.id !== materialId)
    }));
  };

  const detectVideoType = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    return 'other';
  };

  const handleSave = async () => {
    try {
      let moduleId = editingModule;

      // Salvar ou criar módulo primeiro
      if (isCreating) {
        const response = await axios.post(`${API}/admin/modules`, formData, { withCredentials: true });
        moduleId = response.data.module_id;
        toast.success('Módulo criado com sucesso!');
      } else {
        await axios.put(`${API}/admin/modules/${editingModule}`, formData, { withCredentials: true });
        toast.success('Módulo atualizado com sucesso!');
      }

      // Se houver arquivo de vídeo para upload, usar signed URL
      if (formData.master_video_file && moduleId) {
        const file = formData.master_video_file;

        try {
          // Passo 1: Solicitar URL assinada
          toast.loading('Preparando upload...', { id: 'video-upload' });
          const urlFormData = new FormData();
          urlFormData.append('filename', file.name);
          urlFormData.append('content_type', file.type || 'video/mp4');

          const urlResponse = await axios.post(
            `${API}/admin/modules/${moduleId}/generate-upload-url`,
            urlFormData,
            { withCredentials: true }
          );

          const { upload_url, public_url, headers } = urlResponse.data;

          // Passo 2: Upload direto ao GCS
          toast.loading(`Enviando vídeo (${(file.size / 1024 / 1024).toFixed(1)}MB)...`, { id: 'video-upload' });

          await fetch(upload_url, {
            method: 'PUT',
            headers: headers,
            body: file
          });

          // Passo 3: Confirmar upload no backend
          toast.loading('Finalizando...', { id: 'video-upload' });
          const confirmFormData = new FormData();
          confirmFormData.append('public_url', public_url);
          confirmFormData.append('filename', file.name);

          await axios.post(
            `${API}/admin/modules/${moduleId}/confirm-upload`,
            confirmFormData,
            { withCredentials: true }
          );

          toast.success('Vídeo enviado com sucesso!', { id: 'video-upload' });
        } catch (videoError) {
          console.error('Error uploading video:', videoError);
          toast.error('Erro ao enviar vídeo', { id: 'video-upload' });
          throw videoError;
        }
      }

      setEditingModule(null);
      setIsCreating(false);
      fetchModules();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error('Erro ao salvar módulo');
    }
  };

  const handleDelete = async (moduleId) => {
    if (!window.confirm('Tem certeza que deseja excluir este módulo?')) return;

    try {
      await axios.delete(`${API}/admin/modules/${moduleId}`, { withCredentials: true });
      toast.success('Módulo excluído com sucesso!');
      fetchModules();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Erro ao excluir módulo');
    }
  };

  const handleCancel = () => {
    setEditingModule(null);
    setIsCreating(false);
  };

  const addTopic = (contentType) => {
    const key = contentType === 'essential' ? 'essential_content' : 'complementary_content';
    setFormData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        topics: [...(prev[key].topics || []), '']
      }
    }));
  };

  const updateTopic = (contentType, index, value) => {
    const key = contentType === 'essential' ? 'essential_content' : 'complementary_content';
    const newTopics = [...formData[key].topics];
    newTopics[index] = value;
    setFormData(prev => ({
      ...prev,
      [key]: { ...prev[key], topics: newTopics }
    }));
  };

  const removeTopic = (contentType, index) => {
    const key = contentType === 'essential' ? 'essential_content' : 'complementary_content';
    const newTopics = formData[key].topics.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      [key]: { ...prev[key], topics: newTopics }
    }));
  };

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen">
        <Navigation user={user} />
        <div className="container mx-auto px-4 md:px-8 py-16">
          <Card className="p-8 rounded-none border border-border text-center">
            <h2 className="text-2xl font-bold mb-2">Acesso Negado</h2>
            <p className="text-muted-foreground">Você não tem permissão para acessar esta área.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation user={user} />

      <main className="container mx-auto px-4 md:px-8 py-16">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="modules-manager-title">
              Gerenciar Módulos
            </h1>
            <p className="text-muted-foreground">Crie e edite os módulos do programa AIFT</p>
          </div>
          <Button
            onClick={handleCreate}
            className="rounded-none font-medium tracking-wide uppercase text-xs"
            data-testid="create-module-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Módulo
          </Button>
        </div>

        {(isCreating || editingModule) && (
          <Card className="p-8 rounded-none border-2 border-primary mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isCreating ? 'Criar Novo Módulo' : 'Editar Módulo'}
              </h2>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Ordem</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className="rounded-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Título *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Módulo 1: Singularidade, Futuro e Tendências de IA"
                  className="rounded-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Subtítulo *</label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Ex: A Nova Fronteira da Inteligência Artificial"
                  className="rounded-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Descrição *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do módulo..."
                  className="rounded-none min-h-[100px]"
                />
              </div>

              <div className="border border-border p-4 rounded-none space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Video className="h-4 w-4" />
                  <label className="text-sm font-medium">Vídeo da Aula Master</label>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Opção 1: Cole a URL do YouTube ou Vimeo</Label>
                    <Input
                      value={formData.master_video_url}
                      onChange={(e) => {
                        const url = e.target.value;
                        setFormData({
                          ...formData,
                          master_video_url: url,
                          master_video_type: detectVideoType(url)
                        });
                      }}
                      placeholder="https://youtube.com/watch?v=..."
                      className="rounded-none"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs text-muted-foreground">ou</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Opção 2: Faça upload de um arquivo de vídeo</Label>
                    <div className="flex gap-2">
                      <Input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.avi,.mkv"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setFormData({
                              ...formData,
                              master_video_file: file,
                              master_video_filename: file.name,
                              master_video_type: 'upload'
                            });
                          }
                        }}
                        className="rounded-none"
                      />
                    </div>
                    {formData.master_video_filename && formData.master_video_type === 'upload' && (
                      <p className="text-xs text-green-600 mt-1">
                        📹 Arquivo selecionado: {formData.master_video_filename}
                      </p>
                    )}
                  </div>
                </div>

                {formData.master_video_url && formData.master_video_type !== 'upload' && (
                  <p className="text-xs text-muted-foreground">
                    🎬 Tipo detectado: {formData.master_video_type || 'outro'}
                  </p>
                )}
              </div>

              <div className="border border-border p-4 rounded-none space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" />
                  <label className="text-sm font-medium">Materiais Essenciais</label>
                </div>
                <div className="space-y-2">
                  {(formData.essential_materials || []).map((material) => (
                    <div key={material.id} className="flex items-center gap-2 bg-muted/50 p-2">
                      <Link className="h-3 w-3" />
                      <span className="flex-1 text-sm">{material.title}</span>
                      <a href={material.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                        Abrir
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => removeMaterial('essential', material.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Título do material"
                    value={newMaterial.title}
                    onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
                    className="rounded-none flex-1"
                  />
                  <Input
                    placeholder="URL"
                    value={newMaterial.url}
                    onChange={(e) => setNewMaterial({ ...newMaterial, url: e.target.value })}
                    className="rounded-none flex-1"
                  />
                  <Button size="sm" variant="outline" onClick={() => addMaterialLink('essential')} className="rounded-none">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div className="border border-border p-4 rounded-none space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" />
                  <label className="text-sm font-medium">Materiais Complementares</label>
                </div>
                <div className="space-y-2">
                  {(formData.complementary_materials || []).map((material) => (
                    <div key={material.id} className="flex items-center gap-2 bg-muted/50 p-2">
                      <Link className="h-3 w-3" />
                      <span className="flex-1 text-sm">{material.title}</span>
                      <a href={material.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                        Abrir
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => removeMaterial('complementary', material.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Título do material"
                    value={newMaterial.title}
                    onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
                    className="rounded-none flex-1"
                  />
                  <Input
                    placeholder="URL"
                    value={newMaterial.url}
                    onChange={(e) => setNewMaterial({ ...newMaterial, url: e.target.value })}
                    className="rounded-none flex-1"
                  />
                  <Button size="sm" variant="outline" onClick={() => addMaterialLink('complementary')} className="rounded-none">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">URL da Aula ao Vivo (Zoom/Meet)</label>
                <Input
                  value={formData.live_session_url}
                  onChange={(e) => setFormData({ ...formData, live_session_url: e.target.value })}
                  placeholder="https://zoom.us/j/..."
                  className="rounded-none"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">Tópicos do Conteúdo Essencial</label>
                  <Button size="sm" variant="outline" onClick={() => addTopic('essential')} className="rounded-none">
                    <Plus className="h-3 w-3 mr-1" /> Adicionar
                  </Button>
                </div>
                {formData.essential_content.topics.map((topic, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={topic}
                      onChange={(e) => updateTopic('essential', index, e.target.value)}
                      placeholder="Tópico..."
                      className="rounded-none"
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeTopic('essential', index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">Tópicos do Conteúdo Complementar</label>
                  <Button size="sm" variant="outline" onClick={() => addTopic('complementary')} className="rounded-none">
                    <Plus className="h-3 w-3 mr-1" /> Adicionar
                  </Button>
                </div>
                {formData.complementary_content.topics.map((topic, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={topic}
                      onChange={(e) => updateTopic('complementary', index, e.target.value)}
                      placeholder="Tópico..."
                      className="rounded-none"
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeTopic('complementary', index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 pt-4">
                <Button onClick={handleCancel} variant="outline" className="flex-1 rounded-none">
                  Cancelar
                </Button>
                <Button onClick={handleSave} className="flex-1 rounded-none">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Módulo
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="space-y-4">
          {modules.map((module) => (
            <Card key={module.module_id} className="p-6 rounded-none border border-border hover:border-primary/50 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                    Ordem: {module.order}
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {module.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">{module.subtitle}</p>
                  <p className="text-sm">{module.description}</p>
                  {module.live_session_url && (
                    <p className="text-xs text-muted-foreground mt-2">🔗 URL da aula configurada</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(module)}
                    className="rounded-none"
                    data-testid={`edit-module-${module.module_id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(module.module_id)}
                    className="rounded-none text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}